import { state } from '../state';
import type { Item, LockLineSegment } from '../types';
import { dom } from '../dom';
import { CX, CY,
  INSTALLER_SCANNER_DIM_LERP,
  UNLOCK_DURATION_MS, LOCKING_DURATION_MS,
  LOCK_ICON_REVEAL_START,
  LOCK_LINE_ALPHA,
  LOCK_LINE_CENTER_PULL_MAX, LOCK_LINE_START_HIDE_FACTOR, HOME_LINE_START_HIDE_FACTOR,
  LOCK_LINE_COLLIDER_WIDTH,
  LOCK_HOUR_LINE_NUB_INSET, LOCK_HOUR_LINE_WIDTH,
  FEEDBACK_PRESS_SCALES,
  HOME_OPTIONS_EXIT_MS,
  HOME_OPTIONS_REMOVE_DELAY_FRAC,
  HOME_EDIT_DIM_STRENGTH,
  HOME_EDIT_DIM_LERP,
  MAX_LINE_ROWS,
} from '../config';
import { lerp, clamp, smoothstep, easeOutCubic } from '../utils/math';
import { hexToRgba, tintHexWithBlack } from '../utils/color';
import { LINE_CENTER_RADIUS } from '../layout/ringLayout';
import {
  isScannerCameraLockActive,
  getItemSettledScale, getItemRenderScale,
  applyItemTransform,
} from '../layout/ringLayout';

export const CANVAS_SCALE = 2;         // 2x resolution for crisp lines

// Canvas contexts — derived from dom refs
const ctx          = dom.lines.getContext('2d')!;
const lockLinesCtx = dom.lockLinesOverlay.getContext('2d')!;

export function clearLinesCanvas() {
  ctx.clearRect(0, 0, 800 * CANVAS_SCALE, 800 * CANVAS_SCALE);
}

// resetInstallerScannerVisual is still in index.html; wired via setResetInstallerScannerVisualRef()
let resetInstallerScannerVisual: (item: Item) => void = (_item) => {};
export function setResetInstallerScannerVisualRef(fn: (item: Item) => void): void {
  resetInstallerScannerVisual = fn;
}

export function applyHomeScannerDimming() {
  const dimActive = isScannerCameraLockActive();
  const dimTarget = clamp(state.installerScannerDimStrength, 0, 0.95);
  const lineTarget = (dimActive && state.installerItem) ? dimTarget : 0;
  const nextLineTint = lerp(state.installerScannerLineTint, lineTarget, INSTALLER_SCANNER_DIM_LERP);
  state.installerScannerLineTint = Math.abs(nextLineTint - lineTarget) < 0.003
    ? lineTarget
    : nextLineTint;

  // Edit-mode dim: every app except the focused one goes darker so the
  // focus stands out. The focus chain is dragItem (when dragging) → live
  // highlight → original targetItem. The targetItem fallback matters: the
  // cursor-driven highlight goes null the moment the cursor leaves an
  // icon, and without a fallback the dim would briefly drop off everyone
  // and re-engage on the next frame — perceptually a flicker that reads
  // as "no smoothing."
  const editMode = state.homeUI.mode === 'edit_mode';
  const editFocus = editMode
    ? (state.homeUI.dragItem ?? state.highlighted ?? state.homeUI.targetItem)
    : null;

  // Use the slower edit-mode lerp whenever the installer scanner isn't
  // actively driving the dim. This covers entry into edit mode (target
  // 0→0.4) and exit (0.4→0), plus the dim/bright transfer when the focus
  // changes inside edit mode — all on the same ~600ms curve. The fast
  // INSTALLER_SCANNER_DIM_LERP only kicks in while the scanner itself is
  // active (different UX intent — reactive feedback, not a deliberate
  // mode change).
  const installerDimActive = dimActive && !!state.installerItem;
  const itemDimLerp = installerDimActive ? INSTALLER_SCANNER_DIM_LERP : HOME_EDIT_DIM_LERP;

  for (const item of state.items) {
    if (!item || !item.inner) continue;
    let targetTint = 0;
    if (installerDimActive && item !== state.installerItem) {
      targetTint = dimTarget;
    } else if (editMode && item !== editFocus) {
      // editFocus may be null (no targetItem yet); in that case dim every
      // item — having something dimmed is preferable to no dim at all.
      targetTint = HOME_EDIT_DIM_STRENGTH;
    }
    const currentTint = Number.isFinite(item.visualTint) ? item.visualTint : 0;
    const nextTint = lerp(currentTint, targetTint, itemDimLerp);
    item.visualTint = Math.abs(nextTint - targetTint) < 0.003
      ? targetTint
      : nextTint;

    const brightness = clamp(1 - item.visualTint, 0.05, 1);
    item.inner.style.filter = brightness >= 0.999
      ? ''
      : `brightness(${brightness.toFixed(3)})`;
  }
}

export function clearLockLinesOverlay() {
  if (!lockLinesCtx) return;
  lockLinesCtx.clearRect(0, 0, 800 * CANVAS_SCALE, 800 * CANVAS_SCALE);
}

export function getUnlockProgress(now: number) {
  if (state.appState !== 'unlocking') return state.appState === 'home' ? 1 : 0;
  return clamp((now - state.unlockStartTime) / UNLOCK_DURATION_MS, 0, 1);
}

export function getLockingProgress(now: number) {
  if (state.appState !== 'locking') return 0;
  return clamp((now - state.lockingStartTime) / LOCKING_DURATION_MS, 0, 1);
}

export function getIconRevealProgress(pullbackProgress: number) {
  const normalized = clamp(
    (pullbackProgress - LOCK_ICON_REVEAL_START) / (1 - LOCK_ICON_REVEAL_START),
    0,
    1
  );
  return smoothstep(normalized);
}

export function applyLockItemReveal(reveal: number) {
  // Items are always at their settled scale and final position — no
  // per-frame scale ramp. Visibility is driven purely by opacity, which
  // the .item CSS rule cross-fades via `transition: opacity 320ms`. This
  // avoids the "pop" that scale-0-to-settled produces on low-end devices
  // where the compositor can render the scale jump as a single chunky
  // frame.
  //
  // - Unlocking: opacity 1 (items fully shown; watchface is animating out)
  // - Locking:   opacity = reveal (ramps down as the lock scene returns)
  // - Lock:      opacity 0 at rest (items hidden behind watchface)
  const isUnlocking = state.appState === 'unlocking';
  const opacity = isUnlocking ? 1 : reveal;
  for (const item of state.items) {
    item.currentX = item.baseX;
    item.currentY = item.baseY;
    item.targetX = item.baseX;
    item.targetY = item.baseY;
    item.currentScale = getItemSettledScale(item);
    if (item.scannerLayerEl) resetInstallerScannerVisual(item);
    else applyItemTransform(item);
    item.visualOpacity = opacity;
    item.el.style.opacity = opacity.toFixed(3);
    item.visualTint = 0;
    if (item.inner) item.inner.style.filter = '';
  }
}

function computeLockHourItems() {
  if (state.lockHourItems) return;
  // Lock-hour nubs come from the outermost populated ring that is also
  // within MAX_LINE_ROWS. Inner rings use the reveal-on-pullback
  // treatment; rings beyond the cap render nothing at all. Inside-out
  // fill means the physical outer ring can be empty, so the hour row
  // is whichever drawn ring currently sits furthest out.
  const maxDrawnRing = MAX_LINE_ROWS - 1;
  let outerRing = -1;
  for (const item of state.items) {
    if (item.ringIndex > maxDrawnRing) continue;
    if (item.ringIndex > outerRing) outerRing = item.ringIndex;
  }
  state.lockHourItems = outerRing < 0
    ? []
    : state.items.filter(item => item.ringIndex === outerRing);
}

function getLockLineSegments(pullbackProgress: number) {
  computeLockHourItems();
  const lockItemSet = new Set(state.lockHourItems ?? []);

  const pressAmount = clamp((1 - state.feedbackScaleCurrent) / (1 - FEEDBACK_PRESS_SCALES[FEEDBACK_PRESS_SCALES.length - 1]!), 0, 1);
  const pullBlend = state.appState === 'lock' ? pressAmount : lerp(pressAmount, 0, pullbackProgress);
  const centerPull = LOCK_LINE_CENTER_PULL_MAX * pullBlend;
  const lineStartHideFactor = lerp(LOCK_LINE_START_HIDE_FACTOR, HOME_LINE_START_HIDE_FACTOR, pullbackProgress);

  // During locking, double the retraction so lines pull back faster toward viewport edge
  const nubMult = (state.appState === 'locking') ? 2 : 1;

  // Outer ring (clock-hour lines): nub inset fades from visible nub to flush with center circle
  const outerNubMult = (state.appState === 'locking') ? 2.4 : 1;
  const outerNubInset = LOCK_HOUR_LINE_NUB_INSET * nubMult * outerNubMult * (1 - clamp(pullbackProgress / LOCK_ICON_REVEAL_START, 0, 1));

  // Inner/middle rings: start hidden beyond viewport, grow inward during transition
  const innerRevealT = clamp((pullbackProgress - LOCK_ICON_REVEAL_START) / (1 - LOCK_ICON_REVEAL_START), 0, 1);
  const innerNubInset = 25 * nubMult * (1 - innerRevealT);

  const segments: LockLineSegment[] = [];

  for (const item of state.items) {
    if (item.ringIndex >= MAX_LINE_ROWS) continue;
    const dx = item.baseX - CX;
    const dy = item.baseY - CY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 1) continue;

    const nx = dx / dist;
    const ny = dy / dist;
    const visualRadius = (item.size * Math.max(item.currentScale, 0.001)) / 2;
    const iconEdgeDist = dist - visualRadius;
    const fullLineStartDist = iconEdgeDist - visualRadius * lineStartHideFactor;

    const isOuterRing = lockItemSet.has(item);
    const nubInset = isOuterRing ? outerNubInset : innerNubInset;
    const lineEndDist = Math.max(16, LINE_CENTER_RADIUS - centerPull) + nubInset;
    if (fullLineStartDist <= lineEndDist) continue;

    // Outer end always at item edge — viewport clips it during lock
    const x1 = CX + nx * fullLineStartDist;
    const y1 = CY + ny * fullLineStartDist;
    const x2 = CX + nx * lineEndDist;
    const y2 = CY + ny * lineEndDist;

    const baseLineColor = item.sampledLineColor || item.color;
    const cr = parseInt(baseLineColor.slice(1,3),16), cg = parseInt(baseLineColor.slice(3,5),16), cb = parseInt(baseLineColor.slice(5,7),16);
    const segColor = (cr + cg + cb) < 140 ? '#666666' : baseLineColor;
    segments.push({ x1, y1, x2, y2, color: segColor });
  }

  return segments;
}

export function drawLockLinesOverlay(pullbackProgress: number) {
  state.lockLineSegments = getLockLineSegments(pullbackProgress);
  if (!lockLinesCtx || !dom.lockLinesOverlay) return;
  clearLockLinesOverlay();

  dom.lockLinesOverlay.style.opacity = '1';
  const lineAlpha = LOCK_LINE_ALPHA;

  lockLinesCtx.save();
  lockLinesCtx.scale(CANVAS_SCALE, CANVAS_SCALE);
  lockLinesCtx.lineCap = 'round';

  const widthBlend = clamp(pullbackProgress / LOCK_ICON_REVEAL_START, 0, 1);
  const lineWidth = lerp(LOCK_HOUR_LINE_WIDTH, 2.25, widthBlend);

  for (const seg of state.lockLineSegments) {
    lockLinesCtx.beginPath();
    lockLinesCtx.moveTo(seg.x2, seg.y2);
    lockLinesCtx.lineTo(seg.x1, seg.y1);
    lockLinesCtx.strokeStyle = hexToRgba(seg.color, lineAlpha);
    lockLinesCtx.lineWidth = lineWidth;
    lockLinesCtx.stroke();
  }

  lockLinesCtx.restore();
}

function transformOverlayPointToLockPhysics(x: number, y: number, overlayScale: number, offsetX: number, offsetY: number, lockScreenScale: number) {
  const overlayX = CX + (x - CX) * overlayScale + offsetX;
  const overlayY = CY + (y - CY) * overlayScale + offsetY;
  const invLockScale = 1 / Math.max(0.001, lockScreenScale);
  return {
    x: CX + (overlayX - CX) * invLockScale,
    y: CY + (overlayY - CY) * invLockScale,
  };
}

export function syncLockLineColliders(lockScreenScale: number) {
  if (!state.watchfaceHost) return;
  if ((state.appState !== 'lock' && state.appState !== 'locking') || state.lockLineSegments.length === 0) {
    state.watchfaceHost.setLockLineColliders([], { enabled: false });
    return;
  }

  const overlayScale = state.worldZoom * state.menuScale;
  const offsetX = -state.cameraX;
  const offsetY = -state.cameraY;
  const transformedSegments: LockLineSegment[] = [];

  for (const seg of state.lockLineSegments) {
    const p1 = transformOverlayPointToLockPhysics(seg.x1, seg.y1, overlayScale, offsetX, offsetY, lockScreenScale);
    const p2 = transformOverlayPointToLockPhysics(seg.x2, seg.y2, overlayScale, offsetX, offsetY, lockScreenScale);
    transformedSegments.push({ x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, color: seg.color });
  }

  state.watchfaceHost.setLockLineColliders(transformedSegments, {
    enabled: true,
    thickness: LOCK_LINE_COLLIDER_WIDTH,
  });
}

export function drawLines() {
  ctx.clearRect(0, 0, 800 * CANVAS_SCALE, 800 * CANVAS_SCALE);

  ctx.save();
  ctx.scale(CANVAS_SCALE, CANVAS_SCALE);
  const lineTintStrength = clamp(state.installerScannerLineTint, 0, 0.95);

  // Per-frame removal progress for the item being removed, shared with the
  // scale/opacity animation in homeOptions.ts (same curve, same clock).
  // Drives the line-trim from center → app so the line vanishes in step
  // with the icon shrinking.
  const removingItem = state.homeUI.removingItem;
  let removeTrimT = 0;
  if (removingItem && state.homeUI.mode === 'options_exiting' && state.homeUI.exitStartedAt > 0) {
    const raw = clamp((performance.now() - state.homeUI.exitStartedAt) / HOME_OPTIONS_EXIT_MS, 0, 1);
    const span = 1 - HOME_OPTIONS_REMOVE_DELAY_FRAC;
    const removeT = clamp((raw - HOME_OPTIONS_REMOVE_DELAY_FRAC) / span, 0, 1);
    removeTrimT = easeOutCubic(removeT);
  }

  for (const item of state.items) {
    if (item.ringIndex >= MAX_LINE_ROWS) continue;
    const sx = item.currentX;
    const sy = item.currentY;

    const dx = sx - CX;
    const dy = sy - CY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 1) continue;

    const nx = dx / dist;
    const ny = dy / dist;

    const visualRadius = (item.size * getItemRenderScale(item)) / 2;

    const iconEdgeDist = dist - visualRadius;
    const lineStartDist = iconEdgeDist - visualRadius * HOME_LINE_START_HIDE_FACTOR;
    let lineEndDist = LINE_CENTER_RADIUS;

    // Removing item: pull the inner end of the line outward toward the
    // outer end so the line retracts from center → app and disappears
    // alongside the shrinking icon.
    if (item === removingItem && removeTrimT > 0) {
      lineEndDist = lerp(LINE_CENTER_RADIUS, Math.max(lineStartDist, LINE_CENTER_RADIUS), removeTrimT);
    }

    if (lineStartDist <= lineEndDist) continue;

    const x1 = CX + nx * lineStartDist;
    const y1 = CY + ny * lineStartDist;
    const x2 = CX + nx * lineEndDist;
    const y2 = CY + ny * lineEndDist;

    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(x1, y1);

    // Prefer a colour sampled from the cover image (set asynchronously when
    // the cover loads); fall back to item.color when no cover is present.
    // Use grey if the resolved colour is too dark to show on black.
    const c = item.sampledLineColor || item.color;
    const r = parseInt(c.slice(1,3),16), g = parseInt(c.slice(3,5),16), b = parseInt(c.slice(5,7),16);
    const lineColor = (r + g + b) < 140 ? '#666666' : c;
    // Per-line tint follows the item's already-lerped visualTint, so the
    // line's darkening matches the icon's darkening exactly (smooth, not a
    // step). The global installer line tint still applies as a floor.
    const itemTint = Number.isFinite(item.visualTint) ? clamp(item.visualTint, 0, 0.95) : 0;
    const perLineTint = Math.max(lineTintStrength, itemTint);
    const tintedLineColor = tintHexWithBlack(lineColor, perLineTint);

    if (item === state.highlighted) {
      ctx.strokeStyle = tintedLineColor;
      ctx.lineWidth = 3;
    } else {
      ctx.strokeStyle = tintedLineColor;
      ctx.lineWidth = 2.25;
    }

    ctx.stroke();
  }

  ctx.restore();
}
